import Tab from '@jetbrains/ring-ui/components/tabs/tab'
import {Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import classNames from 'classnames'
import React, {Suspense} from 'react'
import {EntryPointContainer} from 'react-relay'
import type {PreloadedEntryPoint} from 'react-relay/relay-hooks/EntryPointTypes'

import {getHrefWithQueryParams} from '../../../../../routes'
import type {BuildId} from '../../../../../types'
import {BuildPageTabNamesEnum} from '../../../../../types'
import {base_uri} from '../../../../../types/BS_types'
import IframeAdapter from '../../../../common/IframeAdapter/IframeAdapter'
import Loader from '../../../../common/Loader/Loader'
import {BuildTabs} from '../../../../common/ToggleSakuraUI/ToggleSakuraUI.utils'
import BuildArtifactsTab from '../../../BuildPage/BuildArtifactsTab/BuildArtifactsTab'
import BuildLogTab from '../../../BuildPage/BuildLogTab/BuildLogTab.container'
import type {BuildOverviewTabOuterContainerComponent} from '../../../BuildPage/BuildOverviewTab/BuildOverviewTab.container'
import type {BuildTestsTabComponent} from '../../../BuildPage/BuildTestsTab/BuildTestsTab'

import {supportedTabs} from './SelectedJob.consts'
import useSelectedJob from './SelectedJob.hooks'

import styles from './SelectedJob.css'

type Props = {
  jobId: BuildId
  overviewTabEntryPoint?: PreloadedEntryPoint<BuildOverviewTabOuterContainerComponent>
  testsTabEntryPoint?: PreloadedEntryPoint<BuildTestsTabComponent>
}

function SelectedJob({jobId, overviewTabEntryPoint, testsTabEntryPoint}: Props) {
  const {isFinished, searchParams, navigate, location, isLoading, tabs, iframe, focusIframe} =
    useSelectedJob(jobId)

  if (isLoading) {
    return (
      <div className={styles.popup}>
        <Loader />
      </div>
    )
  }

  const filteredTabs = tabs?.filter(tab => supportedTabs.includes(BuildTabs[tab.id]!)) ?? []
  const selectedTab = searchParams.get('tab') ?? filteredTabs[0]?.id
  const allTabs =
    !isFinished || selectedTab === 'game'
      ? filteredTabs.concat({id: 'game', title: 'Game'})
      : filteredTabs

  return (
    <div className={classNames(styles.popup, {[styles.gamePopup]: selectedTab === 'game'})}>
      <Tabs selected={selectedTab} className={styles.tabs}>
        {allTabs.map(tab => {
          const href = getHrefWithQueryParams(location, null, params => ({...params, tab: tab.id}))
          return (
            <Tab
              key={tab.id}
              id={tab.id}
              title={tab.title}
              href={href}
              titleProps={{
                onPlainLeftClick: () => navigate(href, {replace: selectedTab === tab.id}),
              }}
            >
              <Suspense fallback={<Loader />}>
                {BuildTabs[tab.id] === BuildPageTabNamesEnum.OVERVIEW && overviewTabEntryPoint && (
                  <EntryPointContainer
                    entryPointReference={overviewTabEntryPoint}
                    props={{buildId: jobId}}
                  />
                )}
                {BuildTabs[tab.id] === BuildPageTabNamesEnum.LOG && <BuildLogTab buildId={jobId} />}
                {BuildTabs[tab.id] === BuildPageTabNamesEnum.TESTS && testsTabEntryPoint && (
                  <EntryPointContainer
                    entryPointReference={testsTabEntryPoint}
                    props={{buildId: jobId}}
                  />
                )}
                {BuildTabs[tab.id] === BuildPageTabNamesEnum.ARTIFACTS && (
                  <BuildArtifactsTab buildId={jobId} />
                )}
                {tab.id === 'game' && (
                  <IframeAdapter
                    iframeRef={iframe}
                    className={styles.game}
                    title="2048"
                    src={`${base_uri}/js/ring/2048/index.htm`}
                    onIframeLoaded={focusIframe}
                  />
                )}
              </Suspense>
            </Tab>
          )
        })}
      </Tabs>
    </div>
  )
}

export default SelectedJob
